import { HasSearchCriteria } from '@App/stores/search/helpers/has-search-criteria'
import SearchCriteriaStore from '@App/stores/search/search-criteria-store'
import { SearchOptionType } from '@App/enum/search-option-type'
import { RequiredCriteria } from '@App/enum/required-criteria'
import RootStore from '@App/stores/root-store'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { PaginationBody, PaginationInfo } from '@Entities/pagination'
import { getNumTitles, INumTitles } from '@App/helpers/pagination/get-num-titles'
import { calcNumPages } from '@App/helpers/pagination/calc-num-pages'
import { findPageOnLimitChange } from '@App/helpers/pagination/find-page-on-limit-change'
import { WorkSearch } from '@App/stores/search/models/work-search'
import { searchWorks } from '@App/api/routes/search'
import { Works } from '@Entities/work/works'
import { SEARCH_WORK_OPTIONS } from '@App/stores/search/public/public-search-works-store'
import { mapCriteriaToObject } from '@App/helpers/map-criteria-to-object'

export default class CreateWorkSearchStore implements HasSearchCriteria<WorkSearch> {
    createIndividualWorkRegistrationSearchCriteriaStore(): SearchCriteriaStore<WorkSearch> {
        const workSearchMaxCriteria = 5
        return new SearchCriteriaStore(
            [
                {
                    id: SEARCH_WORK_OPTIONS[0].id,
                    searchTerm: '',
                    isFixed: true,
                    selectedOption: SEARCH_WORK_OPTIONS[0],
                },
                {
                    id: SEARCH_WORK_OPTIONS[1].id,
                    searchTerm: '',
                    isFixed: true,
                    selectedOption: SEARCH_WORK_OPTIONS[1],
                },
            ],
            SEARCH_WORK_OPTIONS,
            SearchOptionType.primary,
            RequiredCriteria.all,
            workSearchMaxCriteria
        )
    }

    private rootStore: RootStore

    private _createWorkSearchCriteriaStore: SearchCriteriaStore<WorkSearch>

    private _createWorkSearchQuery: WorkSearch

    private _createWorkSearchResults: Works | undefined

    private _createWorkSearchPaginationInfo: PaginationInfo | undefined

    private _currentResultsPerPageLimit: number

    private _currentPage: number

    private _redirectingToCatalog = false

    private _workSearch: WorkSearch = {
        title: '',
        writerFullNames: '',
    }

    constructor(rootStore: RootStore) {
        makeObservable<
            CreateWorkSearchStore,
            | '_createWorkSearchResults'
            | '_workSearch'
            | '_createWorkSearchCriteriaStore'
            | '_createWorkSearchQuery'
            | '_currentPage'
            | '_currentResultsPerPageLimit'
        >(this, {
            _createWorkSearchResults: observable,
            resetSearchResults: action,
            _createWorkSearchQuery: observable,
            setCreateWorkSearchQuery: action,
            createWorkSearchQuery: computed,
            numTitles: computed,
            _currentPage: observable,
            setCurrentPage: action,
            _currentResultsPerPageLimit: observable,
            onChangeLimit: action,
            clearWorkSearch: action,
            _workSearch: observable,
            setWorkSearch: action,
            _createWorkSearchCriteriaStore: observable,
            resetSearchCriteriaStore: action,
            resetWorkRegistrationSearchCriteriaIfNeeded: action,
            seedSearchFromUrl: action,
        })

        this.rootStore = rootStore
        this.resetWorkRegistrationSearchCriteriaIfNeeded()
    }

    get searchCriteriaStore(): SearchCriteriaStore<WorkSearch> {
        return this._createWorkSearchCriteriaStore
    }

    resetSearchCriteriaStore(): void {
        this._createWorkSearchCriteriaStore = this.createIndividualWorkRegistrationSearchCriteriaStore()
    }

    get createWorkSearchQuery(): WorkSearch {
        return this._createWorkSearchQuery
    }

    get createWorkSearchResults(): Works | undefined {
        return this._createWorkSearchResults
    }

    get createWorkSearchPaginationInfo(): PaginationInfo | undefined {
        return this._createWorkSearchPaginationInfo
    }

    get currentResultsPerPageLimit(): number {
        return this._currentResultsPerPageLimit
    }

    get currentPage(): number {
        return this._currentPage
    }
    setCreateWorkSearchQuery(createWorkSearchQuery: WorkSearch): void {
        this._createWorkSearchQuery = createWorkSearchQuery
    }

    get numTitles(): INumTitles | null {
        return this._createWorkSearchPaginationInfo
            ? getNumTitles(
                  this._createWorkSearchResults?.count ?? 0,
                  this._createWorkSearchPaginationInfo.currentPage,
                  this._currentResultsPerPageLimit
              )
            : null
    }

    /** True once more than the one always-required criterion has a value; folded into pagination for the API. */
    get isRefinedSearch(): boolean {
        return Object.keys(this._createWorkSearchQuery).length > 1
    }

    /**
     * Performs the search request without touching any observable state, so it can be owned by an
     * external cache (TanStack Query).
     */
    public async fetchWorks(query: WorkSearch, pagination: PaginationBody): Promise<Works> {
        const apiResponse = await searchWorks(pagination, query)
        return {
            count: apiResponse.data.totalElements,
            works: apiResponse.data.content,
        }
    }

    /** Stores results fetched elsewhere so components still reading the store stay in sync. */
    public applySearchResults(response: Works, pagination: PaginationBody): void {
        runInAction(() => {
            this._createWorkSearchResults = response
            if (pagination.limit && pagination.page) {
                this._createWorkSearchPaginationInfo = calcNumPages(
                    response.count,
                    pagination.limit,
                    pagination.page,
                    pagination.refined
                )
            }
        })
    }

    public reportSearchError(error: unknown): void {
        this.rootStore.popupStore.setAlertFromError(error)
    }

    public setCurrentPage(page: number) {
        this._currentPage = page
    }

    public onChangeLimit(newLimit: number) {
        if (this._createWorkSearchResults) {
            const pagination: PaginationBody = { page: this._currentPage, limit: this._currentResultsPerPageLimit }
            const newPage = findPageOnLimitChange(pagination, this._createWorkSearchResults.count, newLimit, 1)
            this.setCurrentPage(newPage)
        }
        this._currentResultsPerPageLimit = newLimit
    }

    get redirectingToCatalog(): boolean {
        return this._redirectingToCatalog
    }

    setRedirectingToCatalog(value: boolean): void {
        this._redirectingToCatalog = value
    }

    /**
     * Starts a visit from a link's search terms. Run it before the page first renders: it swaps in a criteria
     * store seeded from the URL and — only if every required row is filled, as the form itself demands — makes
     * the terms the search, so the first request already carries them. Otherwise the form is just pre-filled.
     */
    seedSearchFromUrl(criteria: Record<string, unknown>): void {
        this.resetSearchCriteriaStore()
        this.searchCriteriaStore.applyCriteriaFromObject(criteria)
        this._currentPage = 1
        this.resetSearchResults()
        const rows = this.searchCriteriaStore.searchCriteria
        this._createWorkSearchQuery = this.searchCriteriaStore.isCriteriaSearchReady(rows)
            ? mapCriteriaToObject(rows)
            : {}
    }

    resetWorkRegistrationSearchCriteriaIfNeeded(): void {
        if (!this.redirectingToCatalog) {
            this._currentPage = 1
            this._currentResultsPerPageLimit = 10
            this.resetSearchCriteriaStore()
            this._createWorkSearchQuery = {}
            this.resetSearchResults()
        }
        this.setRedirectingToCatalog(false)
    }

    /** Clears prior results so a new search never shows the previous criteria's results, success or not. */
    public resetSearchResults(): void {
        this._createWorkSearchResults = undefined
        this._createWorkSearchPaginationInfo = undefined
    }

    public clearWorkSearch = () => {
        this._workSearch = {
            title: '',
            writerFullNames: '',
        }
    }

    get workSearch(): WorkSearch {
        return this._workSearch
    }

    public setWorkSearch = (workSearch: WorkSearch) => {
        this._workSearch = workSearch
    }
}
